import React, { useState } from 'react';
import { 
  Kanban, 
  ArrowLeft, 
  Search, 
  Filter, 
  DollarSign, 
  Clock, 
  CheckCircle2, 
  Share2, 
  FileText, 
  ChevronRight, 
  Plus, 
  Camera, 
  Receipt as ReceiptIcon,
  Zap
} from 'lucide-react';
import { PipelineStage, WorkOrder } from '../types';
import { formatCurrencyBRL } from '../../../utils/formatUtils';

export interface PipelineItem {
  id: string;
  quoteId?: string;
  workOrderId?: string;
  title: string;
  clientName: string;
  clientPhone?: string;
  nicheKey: string;
  finalPrice: number;
  stage: PipelineStage;
  updatedAt: string;
}

interface WorkPipelineKanbanProps {
  items: PipelineItem[];
  onMoveStage: (itemId: string, newStage: PipelineStage) => Promise<void> | void;
  onOpenWorkOrder?: (itemId: string) => void;
  onOpenReceipt?: (itemId: string) => void;
  onOpenItem?: (item: PipelineItem) => void;
  onNewQuote?: () => void;
  onBack?: () => void;
  showHeader?: boolean;
}

export default function WorkPipelineKanban({
  items,
  onMoveStage,
  onOpenWorkOrder,
  onOpenReceipt,
  onOpenItem,
  onNewQuote,
  onBack,
  showHeader = false
}: WorkPipelineKanbanProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeMobileStage, setActiveMobileStage] = useState<PipelineStage>('draft');

  const stages: Array<{ 
    stage: PipelineStage; 
    label: string; 
    shortLabel: string;
    icon: string;
    color: string; 
    bgBadge: string;
    nextActionLabel: string;
  }> = [
    { stage: 'draft', label: 'Rascunho', shortLabel: 'Rascunhos', icon: '📝', color: 'text-zinc-700', bgBadge: 'bg-zinc-100', nextActionLabel: 'Enviar Proposta 📤' },
    { stage: 'sent', label: 'Enviado', shortLabel: 'Enviados', icon: '📤', color: 'text-blue-700', bgBadge: 'bg-blue-50 border-blue-200', nextActionLabel: 'Aprovado pelo Cliente ✅' },
    { stage: 'approved', label: 'Aprovado', shortLabel: 'Aprovados', icon: '🤝', color: 'text-amber-800', bgBadge: 'bg-amber-50 border-amber-200', nextActionLabel: 'Iniciar Execução ⚙️' },
    { stage: 'in_progress', label: 'Em Execução', shortLabel: 'Executando', icon: '⚙️', color: 'text-purple-700', bgBadge: 'bg-purple-50 border-purple-200', nextActionLabel: 'Concluir Serviço 🏆' },
    { stage: 'completed', label: 'Concluído', shortLabel: 'Concluídos', icon: '🏆', color: 'text-emerald-700', bgBadge: 'bg-emerald-50 border-emerald-200', nextActionLabel: 'Registrar Pagamento 💰' },
    { stage: 'paid', label: 'Faturado / Quitado', shortLabel: 'Pagos', icon: '💰', color: 'text-green-800', bgBadge: 'bg-green-50 border-green-200', nextActionLabel: 'Finalizado' }
  ];

  const getNextStage = (current: PipelineStage): PipelineStage | null => {
    switch (current) {
      case 'draft': return 'sent';
      case 'sent': return 'approved';
      case 'approved': return 'in_progress';
      case 'in_progress': return 'completed';
      case 'completed': return 'paid';
      default: return null;
    }
  };

  const filteredItems = items.filter(i => 
    i.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="font-sans">
      {/* Header Sticky Opcional (usado apenas se chamado de forma isolada) */}
      {showHeader && (
        <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-zinc-200/80 px-4 h-14 flex items-center justify-between shadow-2xs mb-4">
          <div className="flex items-center gap-2.5 min-w-0">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="p-2 -ml-1 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-xl transition-all active:scale-95 cursor-pointer shrink-0"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <div className="min-w-0">
              <h1 className="text-sm sm:text-base font-black text-zinc-900 truncate">
                Funil de Vendas & Propostas
              </h1>
              <p className="text-[11px] text-zinc-500 truncate font-medium">
                Acompanhamento visual de propostas
              </p>
            </div>
          </div>

          {onNewQuote && (
            <button
              type="button"
              onClick={onNewQuote}
              className="px-3 py-1.5 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-2xs flex items-center gap-1 cursor-pointer active:scale-95 shrink-0"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Novo Orçamento</span>
            </button>
          )}
        </div>
      )}

      {/* Barra de Busca */}
      <div className="max-w-7xl mx-auto pb-4">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <input
            type="text"
            placeholder="Buscar proposta por cliente ou serviço..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full bg-white border border-zinc-200/80 rounded-2xl pl-10 pr-4 py-2.5 text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20 shadow-2xs font-medium"
          />
        </div>
      </div>

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* MODO 1: MOBILE STAGE SWIPER / SELECTOR (Telas pequenas < 1024px)         */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      <div className="lg:hidden space-y-4">
        {/* Barra de Abas das Etapas */}
        <div className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-none">
          {stages.map(col => {
            const count = filteredItems.filter(i => i.stage === col.stage).length;
            const isActive = activeMobileStage === col.stage;

            return (
              <button
                key={col.stage}
                type="button"
                onClick={() => setActiveMobileStage(col.stage)}
                className={`px-3 py-2 rounded-2xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 cursor-pointer shrink-0 ${
                  isActive
                    ? 'bg-zinc-900 text-white shadow-xs font-black'
                    : 'bg-white border border-zinc-200/80 text-zinc-600 hover:bg-zinc-50'
                }`}
              >
                <span>{col.icon}</span>
                <span>{col.label}</span>
                <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-black ${
                  isActive ? 'bg-white/20 text-white' : 'bg-zinc-100 text-zinc-700'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Lista de Cards da Etapa Selecionada no Celular */}
        {(() => {
          const currentStageObj = stages.find(s => s.stage === activeMobileStage) || stages[0];
          const itemsInActiveStage = filteredItems.filter(i => i.stage === activeMobileStage);
          const stageTotal = itemsInActiveStage.reduce((acc, curr) => acc + curr.finalPrice, 0);

          return (
            <div className="bg-zinc-100/80 border border-zinc-200/80 rounded-3xl p-4 space-y-3">
              {/* Header do Card Mobile */}
              <div className="flex items-center justify-between border-b border-zinc-200/60 pb-2.5">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-black text-zinc-900 flex items-center gap-1.5">
                    <span>{currentStageObj.icon}</span>
                    <span>{currentStageObj.label}</span>
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-xs font-black bg-white text-zinc-700 border border-zinc-200 shadow-2xs">
                    {itemsInActiveStage.length} {itemsInActiveStage.length === 1 ? 'proposta' : 'propostas'}
                  </span>
                </div>
                <span className="text-xs font-black text-brand">
                  Total: {formatCurrencyBRL(stageTotal)}
                </span>
              </div>

              {/* Lista */}
              <div className="space-y-3">
                {itemsInActiveStage.length === 0 ? (
                  <div className="py-12 border border-dashed border-zinc-300/80 rounded-2xl text-center p-4 text-xs text-zinc-500 bg-white/60 space-y-1">
                    <p className="font-bold text-zinc-700">Nenhum orçamento nesta etapa</p>
                    <p className="text-[11px] text-zinc-400">Arraste propostas ou crie um novo orçamento para começar.</p>
                  </div>
                ) : (
                  itemsInActiveStage.map(item => {
                    const nextStage = getNextStage(item.stage);
                    const nextStageObj = nextStage ? stages.find(s => s.stage === nextStage) : null;

                    return (
                      <div
                        key={item.id}
                        className="p-4 bg-white border border-zinc-200/80 rounded-2xl space-y-3 shadow-2xs transition-all"
                      >
                        <div>
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest bg-zinc-100 px-2 py-0.5 rounded-md">
                              {item.nicheKey || 'Geral'}
                            </span>
                            <strong className="text-sm font-black text-brand">
                              {formatCurrencyBRL(item.finalPrice)}
                            </strong>
                          </div>
                          <h4 className="text-sm font-extrabold text-zinc-900 leading-snug">{item.clientName}</h4>
                          <p className="text-xs text-zinc-600 line-clamp-2 font-medium mt-0.5">{item.title}</p>
                        </div>

                        {/* Ações Rápidas Mobile */}
                        <div className="pt-2.5 border-t border-zinc-100 flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-1.5">
                            {item.clientPhone && (
                              <button
                                type="button"
                                onClick={() => {
                                  const cleanPhone = item.clientPhone!.replace(/\D/g, '');
                                  const finalPhone = cleanPhone.startsWith('55') && cleanPhone.length > 11 ? cleanPhone : `55${cleanPhone}`;
                                  const text = encodeURIComponent(`Olá, *${item.clientName}*! 👋 Segue o status da sua proposta "${item.title}" no valor de ${formatCurrencyBRL(item.finalPrice)}.`);
                                  window.open(`https://api.whatsapp.com/send?phone=${finalPhone}&text=${text}`, '_blank');
                                }}
                                className="p-2 bg-emerald-50 hover:bg-emerald-100 text-brand rounded-xl transition-colors border border-emerald-200/80 cursor-pointer active:scale-95"
                                title="Enviar pelo WhatsApp"
                              >
                                <Share2 className="h-4 w-4 text-brand" />
                              </button>
                            )}
                            {onOpenWorkOrder && (
                              <button
                                type="button"
                                onClick={() => onOpenWorkOrder(item.id)}
                                className="p-2 bg-zinc-50 hover:bg-zinc-100 text-zinc-700 rounded-xl transition-colors border border-zinc-200 cursor-pointer active:scale-95"
                                title="Abrir Ordem de Serviço"
                              >
                                <Camera className="h-4 w-4 text-zinc-700" />
                              </button>
                            )}
                            {onOpenReceipt && (
                              <button
                                type="button"
                                onClick={() => onOpenReceipt(item.id)}
                                className="p-2 bg-zinc-50 hover:bg-zinc-100 text-zinc-700 rounded-xl transition-colors border border-zinc-200 cursor-pointer active:scale-95"
                                title="Emitir Recibo PIX"
                              >
                                <ReceiptIcon className="h-4 w-4 text-brand" />
                              </button>
                            )}
                          </div>

                          {nextStage && nextStageObj && (
                            <button
                              type="button"
                              onClick={() => {
                                onMoveStage(item.id, nextStage);
                                setActiveMobileStage(nextStage);
                              }}
                              className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-black rounded-xl shadow-2xs flex items-center gap-1.5 transition-all cursor-pointer active:scale-95"
                            >
                              <span>{currentStageObj.nextActionLabel}</span>
                              <ChevronRight className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* MODO 2: DESKTOP KANBAN MULTI-COLUNAS (Telas >= 1024px)                   */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      <div className="hidden lg:block overflow-x-auto pb-4">
        <div className="flex gap-3.5 min-w-[1200px] items-start">
          {stages.map(col => {
            const itemsInStage = filteredItems.filter(i => i.stage === col.stage);
            const totalStageValue = itemsInStage.reduce((acc, curr) => acc + curr.finalPrice, 0);

            return (
              <div
                key={col.stage}
                className="flex-1 min-w-64 bg-zinc-100/70 border border-zinc-200/60 rounded-3xl p-3.5 flex flex-col max-h-[80vh] shadow-2xs"
              >
                {/* Header da Coluna */}
                <div className="flex items-center justify-between border-b border-zinc-200/80 pb-2.5 mb-2.5">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm">{col.icon}</span>
                    <span className={`text-xs font-black ${col.color}`}>
                      {col.label}
                    </span>
                    <span className="px-1.5 py-0.2 rounded-full text-[10px] font-black bg-white text-zinc-700 border border-zinc-200 shadow-2xs">
                      {itemsInStage.length}
                    </span>
                  </div>
                  <span className="text-[11px] font-black text-zinc-700">
                    {formatCurrencyBRL(totalStageValue)}
                  </span>
                </div>

                {/* Cards da Coluna */}
                <div className="flex-1 overflow-y-auto space-y-2.5 pr-0.5 scrollbar-none">
                  {itemsInStage.length === 0 ? (
                    <div className="py-8 border border-dashed border-zinc-200 rounded-2xl text-center p-3 text-[11px] text-zinc-400 bg-white/40">
                      Nenhum item nesta etapa
                    </div>
                  ) : (
                    itemsInStage.map(item => {
                      const nextStage = getNextStage(item.stage);

                      return (
                        <div
                          key={item.id}
                          className="p-3 bg-white border border-zinc-100 hover:border-zinc-300 rounded-2xl space-y-2.5 shadow-2xs hover:shadow-xs transition-all group"
                        >
                          <div>
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <span className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">
                                {item.nicheKey || 'Geral'}
                              </span>
                              <strong className="text-xs font-black text-brand">
                                {formatCurrencyBRL(item.finalPrice)}
                              </strong>
                            </div>
                            <h4 className="text-xs font-extrabold text-zinc-900 leading-snug">{item.clientName}</h4>
                            <p className="text-[11px] text-zinc-500 line-clamp-1 font-medium">{item.title}</p>
                          </div>

                          {/* Ações Rápidas */}
                          <div className="pt-2 border-t border-zinc-100 flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1">
                              {item.clientPhone && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const cleanPhone = item.clientPhone!.replace(/\D/g, '');
                                    const finalPhone = cleanPhone.startsWith('55') && cleanPhone.length > 11 ? cleanPhone : `55${cleanPhone}`;
                                    const text = encodeURIComponent(`Olá, *${item.clientName}*! Segue o status da proposta "${item.title}" no valor de ${formatCurrencyBRL(item.finalPrice)}.`);
                                    window.open(`https://api.whatsapp.com/send?phone=${finalPhone}&text=${text}`, '_blank');
                                  }}
                                  className="p-1.5 bg-emerald-50 hover:bg-emerald-100 text-brand rounded-lg transition-colors border border-emerald-100 cursor-pointer active:scale-95"
                                  title="Chamar no WhatsApp"
                                >
                                  <Share2 className="h-3.5 w-3.5 text-brand" />
                                </button>
                              )}
                              {onOpenWorkOrder && (
                                <button
                                  type="button"
                                  onClick={() => onOpenWorkOrder(item.id)}
                                  className="p-1.5 bg-zinc-50 hover:bg-zinc-100 text-zinc-600 rounded-lg transition-colors border border-zinc-100 cursor-pointer active:scale-95"
                                  title="Abrir OS"
                                >
                                  <Camera className="h-3.5 w-3.5 text-zinc-600" />
                                </button>
                              )}
                              {onOpenReceipt && (
                                <button
                                  type="button"
                                  onClick={() => onOpenReceipt(item.id)}
                                  className="p-1.5 bg-zinc-50 hover:bg-zinc-100 text-zinc-600 rounded-lg transition-colors border border-zinc-100 cursor-pointer active:scale-95"
                                  title="Emitir Recibo PIX"
                                >
                                  <ReceiptIcon className="h-3.5 w-3.5 text-brand" />
                                </button>
                              )}
                            </div>

                            {nextStage && (
                              <button
                                type="button"
                                onClick={() => onMoveStage(item.id, nextStage)}
                                className="px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-brand text-[10px] font-black uppercase tracking-wider rounded-lg border border-emerald-100 flex items-center gap-1 transition-all cursor-pointer active:scale-95"
                              >
                                <span>Avançar</span>
                                <ChevronRight className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
